


































































































import Vue, { PropType } from "vue";
import Page from "@/components/Page.vue";
import IconButton from "@/components/IconButton.vue";
import SyncPartnerEdit from "../components/syncEntityEdits/SyncPartnerEdit.vue";
import SyncInvoiceEdit from "../components/syncEntityEdits/SyncInvoiceEdit.vue";
import SyncStatusBadge from "../components/SyncStatusBadge.vue";
import store, { InterfaceStore } from "../services/InterfaceStore";
import configuration from "@/models/Configuration";
import { t } from "@/plugins/i18n";

/** A részletes szerkesztőben megnyitható szinkron entitás típusok (a route adja át). */
type SyncEntityType = "partner" | "invoice";

enum ConfirmType {
  None = 0,
  ReSync = 1,
  Skip = 2,
}

interface Data {
  store: InterfaceStore;
  valid: boolean;
  showConfirm: boolean;
  confirmType: ConfirmType;
  ConfirmType: typeof ConfirmType;
}

interface Methods {
  handleShowConfirm(type: ConfirmType): void;
  confirm(): Promise<void>;
  /** A szinkron sor legutolsó köztes tábla sorának újratöltése a szerkesztőbe. */
  reloadLatest(): Promise<void>;
  step(id: number): Promise<void>;
  stepFirst(): void;
  stepPrev(): void;
  stepNext(): void;
  stepLast(): void;
  stepLastDone(): void;
}

interface Computed {
  isInvoice: boolean;
  editComponent: string;
  title: string;
  /** A megerősítő szövegekbe illesztett entitás megnevezés. */
  entityName: string;
  /** A megerősítő sáv szövege a kiválasztott művelethez. */
  confirmMessage: string;
  /** Az aktuálisan szerkesztett rekord (típustól függően partner vagy számlafej DTO). */
  current: any;
  history: any[];
  index: number;
  position: number;
  lastDone: any;
  isLatest: boolean;
  /**
   * A szinkron rekordhoz tartozik beemelt (nem hibás rögzítésű) bejövő számla — ilyenkor sem
   * szerkeszteni, sem újraszinkronizálni, sem kihagyni nem lehet többé.
   */
  isImported: boolean;
  /** A köztes tábla sorok közti léptető látszik-e (rendszerkapcsoló + honnan nyitották). */
  isStepperVisible: boolean;
}

interface Props {
  entityType: SyncEntityType;
  /** Csak megtekintés (Előzmények menüpontból nyitva) — minden mező tiltott, művelet nincs. */
  viewOnly: boolean;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  name: "SyncEntityEditor",

  components: {
    Page,
    IconButton,
    SyncPartnerEdit,
    SyncInvoiceEdit,
    SyncStatusBadge,
  },

  props: {
    entityType: { type: String as PropType<SyncEntityType>, default: "partner" },
    viewOnly: { type: Boolean, default: false },
  },

  data() {
    return {
      store,
      valid: true,
      showConfirm: false,
      confirmType: ConfirmType.None,
      ConfirmType,
    };
  },

  /** A csak megtekintés módot a szerkesztő formok a store-on keresztül látják. */
  created() {
    this.store.editorViewOnly = this.viewOnly;
  },

  beforeDestroy() {
    this.store.editorViewOnly = false;
  },

  computed: {
    isInvoice(): boolean {
      return this.entityType === "invoice";
    },

    /**
     * A léptető csak akkor tűnik el, ha az előzmények külön menüpontban vannak ÉS a szerkesztőt a
     * Külső interfészek főoldalról nyitották — ott a rekord aktuális állapotát szerkeszti a
     * felhasználó. Az Előzmények menüpontból nyitott (csak megtekintés) nézetben a léptetés a felület
     * fő funkciója, ezért ott mindig látszik.
     */
    isStepperVisible(): boolean {
      return !configuration.sysParams.ifaceSyncIsSepHistory || this.viewOnly;
    },

    editComponent(): string {
      return this.isInvoice ? "SyncInvoiceEdit" : "SyncPartnerEdit";
    },

    /** Cím típus szerint — számlánál a szinkron iránya is számít (IN → bejövő, OUT → kimenő). */
    title(): string {
      const action = this.viewOnly ? t("Megtekintese") : t("Interface_Szerkesztese");
      if (!this.isInvoice) {
        return `Partner szinkron rekord ${action}`;
      }
      return this.store.editingInvoice.direction === "OUT"
        ? t("Interface_KimenoSzamlaSzinkronRekord", [action])
        : t("Interface_BejovoSzamlaSzinkronRekord", [action]);
    },

    entityName(): string {
      return this.isInvoice ? t("Szamla_3") : "partner";
    },

    confirmMessage(): string {
      switch (this.confirmType) {
        case ConfirmType.ReSync:
          return t("Interface_BiztosHogyUjraszinkronizaljaEztARekordot", [this.entityName]);
        case ConfirmType.Skip:
          return t("Interface_BiztosHogyEztARekordotEzentul", [this.entityName]);
        default:
          return "";
      }
    },

    current(): any {
      return this.isInvoice ? this.store.editingInvoice : this.store.editingPartner;
    },

    history(): any[] {
      return this.isInvoice ? this.store.editingInvoiceHistory : this.store.editingPartnerHistory;
    },

    index(): number {
      return this.isInvoice ? this.store.editingInvoiceIndex : this.store.editingPartnerIndex;
    },

    position(): number {
      return this.isInvoice ? this.store.editingInvoicePosition : this.store.editingPartnerPosition;
    },

    lastDone(): any {
      return this.isInvoice ? this.store.editingInvoiceLastDone : this.store.editingPartnerLastDone;
    },

    isLatest(): boolean {
      return this.isInvoice ? this.store.editingInvoiceIsLatest : this.store.editingPartnerIsLatest;
    },

    /** A beemelt állapot egyelőre csak számlára értelmezett (a partnernél nincs bejövő számla reláció). */
    isImported(): boolean {
      return this.isInvoice && this.store.editingInvoiceIsImported;
    },
  },

  methods: {
    /** Megerősítő sáv megjelenítése a kiválasztott művelethez (None elrejti). */
    handleShowConfirm(type) {
      this.confirmType = type;
      this.showConfirm = type !== ConfirmType.None;
    },

    /**
     * A megerősített művelet végrehajtása:
     *  - ReSync: a backend a DTO-ból új köztes tábla sort hoz létre (számlánál a tételekkel
     *    együtt) és meghívja az IfaceSync API-t.
     *  - Skip: a kezdőfelület „Kihagyva" gombjával azonos backend hívás, egyetlen tétellel.
     * Mindkét művelet új köztes tábla sort hoz létre, ezért siker után a szerkesztőben maradunk és
     * a szinkron sor legutolsó állapotát töltjük be.
     */
    async confirm() {
      const type = this.confirmType;
      this.handleShowConfirm(ConfirmType.None);
      try {
        if (type === ConfirmType.ReSync) {
          await (this.isInvoice ? this.store.downSyncEditingInvoice() : this.store.downSyncEditingPartner());
        } else if (type === ConfirmType.Skip) {
          await (this.isInvoice ? this.store.markEditingInvoiceSkipped() : this.store.markEditingPartnerSkipped());
        } else {
          return;
        }
        await this.reloadLatest();
      } catch (e) {
        console.error(e);
      }
    },

    /**
     * A szinkron sor legutolsó (a művelettel létrejött) köztes tábla sorának betöltése. Az útvonalat
     * is átállítjuk az új azonosítóra, hogy az oldal újratöltése is ezt a sort hozza.
     */
    async reloadLatest() {
      await (this.isInvoice ? this.store.loadEditingInvoiceLatest() : this.store.loadEditingPartnerLatest());

      const id = String(this.current.id);
      if (this.$route.params["id"] !== id) {
        this.$router.replace({ name: this.$route.name ?? undefined, params: { ...this.$route.params, id } });
      }
    },

    /** A megadott köztes tábla sor betöltése a léptetőből — a form reaktívan frissül. */
    async step(id) {
      try {
        await (this.isInvoice ? this.store.loadEditingInvoice(id) : this.store.loadEditingPartner(id));
      } catch (e) {
        console.error(e);
      }
    },

    /** Legelső (legrégebbi) állapot — a lista utolsó eleme (csökkenő id). */
    stepFirst() {
      const h = this.history;
      if (h.length > 0) {
        this.step(h[h.length - 1].id);
      }
    },

    /** Előző (frissebb) állapot — eggyel kisebb index. */
    stepPrev() {
      if (this.index > 0) {
        this.step(this.history[this.index - 1].id);
      }
    },

    /** Következő (régebbi) állapot — eggyel nagyobb index. */
    stepNext() {
      if (this.index >= 0 && this.index < this.history.length - 1) {
        this.step(this.history[this.index + 1].id);
      }
    },

    /** Legutolsó (legfrissebb) állapot — a lista első eleme (csökkenő id). */
    stepLast() {
      const h = this.history;
      if (h.length > 0) {
        this.step(h[0].id);
      }
    },

    /** Legutolsó sikeres (Done) állapot — a history első Done státuszú sora. */
    stepLastDone() {
      if (this.lastDone) {
        this.step(this.lastDone.id);
      }
    },
  },
});
